'use client';

import { useLocale } from '@vimond/i18n';
import { Locale } from '@vimond/i18n/service';
import { addBasePath } from 'next/dist/client/add-base-path';
import NextLink from 'next/link';
import NProgress from 'nprogress';
import { forwardRef } from 'react';

const getURL = (href: string): URL => {
  return new URL(addBasePath(href), location.href);
};

const getHrefWithLocale = (
  href: string,
  locales: Locale[] | undefined,
  locale: string,
): string => {
  const shouldIncludeLocale =
    locales && !locales.some((loc) => href.startsWith(`/${loc.locale}`));
  if (shouldIncludeLocale) {
    return `/${locale}/${href}`;
  }
  return href;
};

const isModifiedEvent = (event: React.MouseEvent): boolean => {
  const eventTarget = event.currentTarget as HTMLAnchorElement | SVGAElement;
  const target = eventTarget.getAttribute('target');
  return (
    (target && target !== '_self') ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey || // triggers resource download
    event.nativeEvent?.button === 1
  );
};

const shouldTriggerStartEvent = (
  href: string,
  clickEvent?: React.MouseEvent,
) => {
  const current = window.location;
  const target = getURL(href);
  if (clickEvent && isModifiedEvent(clickEvent)) return false; // modified events: fallback to browser behaviour
  if (current.origin !== target.origin) return false; // external URL
  if (current.pathname === target.pathname && current.search === target.search)
    return false; // same URL
  return true;
};

type Props = {
  href?: string;
  onClick?: (event: React.MouseEvent) => void;
  passHref?: boolean;
} & Omit<React.ComponentProps<'a'>, 'href'> &
  Omit<React.ComponentProps<typeof NextLink>, 'href'>;

export const Link = forwardRef<HTMLAnchorElement, Props>(function Link(
  { href, onClick, ...rest },
  ref,
) {
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  const useLink = href && href?.startsWith('/');
  const { locale: currentLocale, locales } = useLocale();
  if (!useLink) return <a href={href} onClick={onClick} {...rest} ref={ref} />;

  const hrefWithLocale = getHrefWithLocale(href, locales, currentLocale);

  return (
    <NextLink
      href={hrefWithLocale}
      {...rest}
      onClick={(event) => {
        event.stopPropagation();
        event.nativeEvent.stopPropagation();
        if (typeof event.nativeEvent.preventDefault === 'function') {
          event.nativeEvent.preventDefault();
        }
        if (href && shouldTriggerStartEvent(href, event)) NProgress.start();
        if (onClick) onClick(event);
      }}
      ref={ref}
    />
  );
});
